import type { BankDetector, ParsedStatement } from "@paisa/types"
import * as XLSX from "xlsx"
import { isAmexCreditCard, parseAmexCreditCard } from "./amex/credit-card"
import { isHdfcSavings, parseHdfcSavings } from "./hdfc/savings"
import {
  isSwiggyhdfcCreditCard,
  parseSwiggyhdfcCreditCard,
  parseSwiggyhdfcCreditCardCsv,
} from "./hdfc/swiggy-credit-card"
import { isIciciSavings, parseIciciSavings } from "./icici/savings"
import { isSbiSavings, parseSbiSavings } from "./sbi/savings"

/** All registered bank parsers. Add new banks here. */
const DETECTORS: BankDetector[] = [
  {
    bank: "hdfc",
    accountType: "savings",
    detect: isHdfcSavings,
    parse: parseHdfcSavings,
  },
  {
    bank: "sbi",
    accountType: "savings",
    detect: isSbiSavings,
    parse: parseSbiSavings,
  },
  {
    bank: "icici",
    accountType: "savings",
    detect: isIciciSavings,
    parse: parseIciciSavings,
  },
  {
    bank: "amex",
    accountType: "credit_card",
    detect: isAmexCreditCard,
    parse: parseAmexCreditCard,
  },
  {
    bank: "hdfc",
    accountType: "credit_card",
    detect: isSwiggyhdfcCreditCard,
    parse: parseSwiggyhdfcAuto,
  },
  // Add more: axis, etc.
]

/** Unified XLS/CSV dispatcher for Swiggy HDFC statements */
function parseSwiggyhdfcAuto(data: ArrayBuffer): ParsedStatement {
  // Use magic bytes to avoid decoding the full ArrayBuffer just for format detection.
  const magic = new Uint8Array(data, 0, 2)
  const isXlsx = magic[0] === 0x50 && magic[1] === 0x4b
  if (!isXlsx) {
    const text = new TextDecoder().decode(data)
    return parseSwiggyhdfcCreditCardCsv(text)
  }
  return parseSwiggyhdfcCreditCard(data)
}

/**
 * Auto-detect bank from file content and parse the statement.
 * Returns null if no parser matches.
 */
export function parseStatement(data: ArrayBuffer): ParsedStatement | null {
  // Extract headers + raw text for detection
  const { headers, rawContent } = extractFileInfo(data)

  for (const detector of DETECTORS) {
    if (detector.detect(rawContent, headers)) {
      return detector.parse(data)
    }
  }

  return null
}

/**
 * Detect which bank a file belongs to without parsing transactions.
 * Returns the bank identifier or null.
 */
export function detectBank(data: ArrayBuffer): { bank: string; accountType: string } | null {
  const { headers, rawContent } = extractFileInfo(data)

  for (const detector of DETECTORS) {
    if (detector.detect(rawContent, headers)) {
      return { bank: detector.bank, accountType: detector.accountType }
    }
  }

  return null
}

/** Extract column headers and raw text content from an XLS/CSV file */
function extractFileInfo(data: ArrayBuffer): { headers: string[]; rawContent: string } {
  // Detect XLSX by ZIP/PK magic bytes (0x50 0x4B) — more reliable than content sniffing
  // and avoids the 4096-byte preamble limit for ~|~ CSV detection.
  const magic = new Uint8Array(data, 0, 4)
  const isXlsx = magic[0] === 0x50 && magic[1] === 0x4b

  if (!isXlsx) {
    // Plain text (CSV). Decode up to 16 KB to detect delimiters for long-preamble files.
    const text = new TextDecoder().decode(data.slice(0, 16384))
    if (text.includes("~|~")) {
      return extractTildePipeCsv(text)
    }
    return { headers: [], rawContent: text.slice(0, 2048) }
  }

  try {
    const workbook = XLSX.read(new Uint8Array(data), { type: "array" })
    const sheet = workbook.Sheets[workbook.SheetNames[0]]
    if (!sheet) return { headers: [], rawContent: "" }

    const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" })

    // Collect all text from first 30 rows for detection
    const rawContent = rows
      .slice(0, 30)
      .map((r) => (r ?? []).map((c) => String(c ?? "")).join(" "))
      .join("\n")

    // Find potential header row — look for a row with 4+ non-empty cells
    let headers: string[] = []
    for (const row of rows.slice(0, 30)) {
      const cells = (row ?? []).map((c) => String(c ?? "").trim()).filter(Boolean)
      if (cells.length >= 4) {
        headers = cells
        break
      }
    }

    return { headers, rawContent }
  } catch {
    return { headers: [], rawContent: "" }
  }
}

/** Handle ~|~ delimited CSV (HDFC Swiggy CC format) */
function extractTildePipeCsv(text: string): { headers: string[]; rawContent: string } {
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, 30)
  const rawContent = lines.join("\n")

  let headers: string[] = []
  for (const line of lines) {
    const cells = line
      .split("~|~")
      .map((c) => c.trim())
      .filter(Boolean)
    if (cells.length >= 4) {
      headers = cells
      break
    }
  }

  return { headers, rawContent }
}
